import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { zodValidator } from '@tanstack/zod-adapter';
import { motion, useReducedMotion } from 'framer-motion';
import {
  approveMcpAuthRequestMutation,
  denyMcpAuthRequestMutation,
  getMcpAuthRequestOptions,
} from '@/api';
import type { AuthRequestInfo } from '@/api';
import { useAuth } from '@/lib/auth';
import Loader from '@/components/Loader';
import { Button } from '@/components/ui/button';
import { GreetLogo } from '@/components/brand/GreetLogo';
import { ConfirmCheck } from '@/components/brand/ConfirmCheck';
import { AuthPageShell, EASE_OUT } from '@/components/Auth/AuthPageShell';
import { cn } from '@/lib/utils';

// The OAuth consent screen for the Greet MCP server. The recorder's /oauth/authorize sends the browser
// here with ?request=<id>; approving returns the redirect (with the code) back to the AI app.

const REQUEST_KEY = 'greet_mcp_auth_request';

const searchSchema = z.object({ request: z.string().optional() });

export const Route = createFileRoute('/oauth/authorize')({
  ssr: false,
  validateSearch: zodValidator(searchSchema),
  component: ConsentPage,
  head: () => ({ meta: [{ title: 'Connect an AI app · Greet' }] }),
});

type Access = 'read' | 'write';

const ACCESS_OPTIONS: { value: Access; label: string; detail: string }[] = [
  {
    value: 'read',
    label: 'Read only',
    detail: 'See sessions, applications, scores and recordings',
  },
  {
    value: 'write',
    label: 'Read & write',
    detail: 'Also create sessions, invite applicants and re-run analysis',
  },
];

function readStoredRequest(): string | undefined {
  try {
    return sessionStorage.getItem(REQUEST_KEY) || undefined;
  } catch {
    return undefined;
  }
}

function storeRequest(id: string | undefined) {
  try {
    if (id) sessionStorage.setItem(REQUEST_KEY, id);
    else sessionStorage.removeItem(REQUEST_KEY);
  } catch {
    // Private mode: the login round-trip just loses the request; the app can restart it.
  }
}

function ConsentPage() {
  const { request: fromUrl } = Route.useSearch();
  const requestId = fromUrl || readStoredRequest();
  const { isAuthenticated, isInitialLoading } = useAuth();
  const navigate = useNavigate();
  const isDevPreview = import.meta.env.DEV && requestId?.startsWith('preview');

  useEffect(() => {
    if (isDevPreview || isInitialLoading || isAuthenticated) return;
    // Sign in first; /login brings the member back here and the id waits in sessionStorage.
    storeRequest(requestId);
    navigate({ to: '/login', search: { returnTo: '/oauth/authorize' } });
  }, [isDevPreview, isInitialLoading, isAuthenticated, requestId, navigate]);

  if (isDevPreview) {
    return (
      <AuthPageShell logo={<GreetLogo className="h-7 w-auto" />}>
        <DevPreview variant={requestId!} />
      </AuthPageShell>
    );
  }
  if (isInitialLoading || !isAuthenticated) return <Loader />;

  return (
    <AuthPageShell logo={<GreetLogo className="h-7 w-auto" />}>
      {requestId ? <Consent requestId={requestId} /> : <Expired />}
    </AuthPageShell>
  );
}

function Consent({ requestId }: { requestId: string }) {
  const query = useQuery({
    ...getMcpAuthRequestOptions({ path: { request_id: requestId } }),
    retry: false,
    refetchOnWindowFocus: false,
  });
  const [done, setDone] = useState<string | null>(null);

  const finish = (redirectTo: string) => {
    storeRequest(undefined);
    setDone(redirectTo);
    window.location.assign(redirectTo);
  };

  if (query.isLoading) return <ConsentSkeleton />;
  if (query.isError || !query.data) return <Expired />;
  if (done) return <Connected info={query.data} />;
  return <ConsentForm info={query.data} requestId={requestId} onFinish={finish} />;
}

function ConsentForm({
  info,
  requestId,
  onFinish,
}: {
  info: AuthRequestInfo;
  requestId: string;
  onFinish: (redirectTo: string) => void;
}) {
  const wantsWrite = info.requested_scopes.includes('write');
  const [access, setAccess] = useState<Access>(wantsWrite && info.can_write ? 'write' : 'read');

  const approve = useMutation({
    ...approveMcpAuthRequestMutation(),
    onSuccess: (data) => onFinish(data.redirect_to),
  });
  const deny = useMutation({
    ...denyMcpAuthRequestMutation(),
    onSuccess: (data) => onFinish(data.redirect_to),
  });
  const busy = approve.isPending || deny.isPending;
  const failed = approve.isError || deny.isError;

  return (
    <div>
      <ClientBadge info={info} />
      <h1 className="mt-5 text-[22px] font-semibold leading-tight tracking-tight text-gray-950 text-pretty dark:text-white">
        Connect {info.client_name} to {info.workspace_name}
      </h1>
      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
        It will act as you ({info.user_email}).
      </p>

      <fieldset className="mt-6 space-y-2" disabled={busy}>
        <legend className="sr-only">Access</legend>
        {ACCESS_OPTIONS.map((option) => {
          const disabled = option.value === 'write' && !info.can_write;
          const selected = access === option.value;
          return (
            <label
              key={option.value}
              className={cn(
                'flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition-colors',
                selected
                  ? 'border-gray-950 bg-gray-50 dark:border-white dark:bg-white/5'
                  : 'border-gray-200 hover:border-gray-300 dark:border-white/10 dark:hover:border-white/20',
                disabled && 'cursor-not-allowed opacity-50 hover:border-gray-200',
              )}>
              <input
                type="radio"
                name="access"
                value={option.value}
                checked={selected}
                disabled={disabled}
                onChange={() => setAccess(option.value)}
                className="mt-1 size-4 accent-gray-950 dark:accent-white"
              />
              <span className="min-w-0">
                <span className="block text-[15px] font-medium text-gray-950 dark:text-white">
                  {option.label}
                </span>
                <span className="mt-0.5 block text-[13px] leading-snug text-gray-500 dark:text-gray-400">
                  {disabled ? 'Workspace viewers can only grant read access' : option.detail}
                </span>
              </span>
            </label>
          );
        })}
      </fieldset>

      <p className="mt-5 text-[13px] leading-snug text-gray-500 dark:text-gray-400">
        {isLoopback(info.redirect_host) ? (
          <>After you allow, {info.client_name} on this computer finishes signing in.</>
        ) : info.verified_domain ? (
          <>After you allow, you go back to {info.redirect_host}.</>
        ) : (
          <>
            After you allow, you go back to{' '}
            <span className="font-medium text-gray-700 dark:text-gray-300">{info.redirect_host}</span>. Only
            connect apps you started this from.
          </>
        )}
      </p>

      {failed && (
        <p role="alert" className="mt-3 text-[13px] text-red-600 dark:text-red-400">
          That didn't go through. This link may have expired, start again from {info.client_name}.
        </p>
      )}

      <div className="mt-6 flex gap-2">
        <Button
          variant="outline"
          className="h-11 flex-1"
          disabled={busy}
          onClick={() => deny.mutate({ path: { request_id: requestId } })}>
          Cancel
        </Button>
        <Button
          className="h-11 flex-1"
          disabled={busy}
          onClick={() => approve.mutate({ path: { request_id: requestId }, body: { access } })}>
          {approve.isPending ? 'Connecting…' : info.already_connected ? 'Reconnect' : 'Allow'}
        </Button>
      </div>
    </div>
  );
}

const isLoopback = (host: string) => ['127.0.0.1', 'localhost', '::1', '[::1]'].includes(host);

function ClientBadge({ info }: { info: AuthRequestInfo }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const initial = info.client_name.trim().charAt(0).toUpperCase() || '?';
  return (
    <div className="flex size-12 items-center justify-center overflow-hidden rounded-2xl bg-gray-100 text-lg font-semibold text-gray-700 dark:bg-white/10 dark:text-gray-200">
      {info.logo_uri && !logoFailed ? (
        <img
          src={info.logo_uri}
          alt=""
          className="size-full object-cover"
          onError={() => setLogoFailed(true)}
        />
      ) : (
        initial
      )}
    </div>
  );
}

function Connected({ info }: { info: AuthRequestInfo }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, transform: 'translateY(6px)' }}
      animate={{ opacity: 1, transform: 'translateY(0px)' }}
      transition={{ duration: 0.24, ease: EASE_OUT }}>
      <ConfirmCheck size={64} />
      <h1 className="mt-6 text-[22px] font-semibold tracking-tight text-gray-950 dark:text-white">
        You're connected
      </h1>
      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
        Go back to {info.client_name}. You can close this tab.
      </p>
    </motion.div>
  );
}

function Expired() {
  return (
    <div>
      <h1 className="text-[22px] font-semibold tracking-tight text-gray-950 dark:text-white">
        This link expired
      </h1>
      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
        Connection links last 10 minutes. Start the connection again from your AI app.
      </p>
    </div>
  );
}

function ConsentSkeleton() {
  return (
    <div aria-busy className="animate-pulse">
      <div className="size-12 rounded-2xl bg-gray-100 dark:bg-white/10" />
      <div className="mt-5 h-6 w-4/5 rounded-md bg-gray-100 dark:bg-white/10" />
      <div className="mt-3 h-4 w-1/2 rounded-md bg-gray-100 dark:bg-white/10" />
      <div className="mt-6 h-[68px] rounded-2xl bg-gray-100 dark:bg-white/10" />
      <div className="mt-2 h-[68px] rounded-2xl bg-gray-100 dark:bg-white/10" />
    </div>
  );
}

// Dev only: /oauth/authorize?request=preview | preview-viewer | preview-done | preview-expired
const PREVIEW_INFO: AuthRequestInfo = {
  request_id: 'preview',
  client_name: 'Claude Code',
  client_uri: null,
  logo_uri: null,
  redirect_host: '127.0.0.1',
  verified_domain: false,
  requested_scopes: ['read', 'write'],
  can_write: true,
  user_email: 'maya@northwind.health',
  workspace_name: 'Northwind Telehealth',
  already_connected: false,
};

function DevPreview({ variant }: { variant: string }) {
  if (variant === 'preview-expired') return <Expired />;
  if (variant === 'preview-done') return <Connected info={PREVIEW_INFO} />;
  if (variant === 'preview-loading') return <ConsentSkeleton />;
  const info = variant === 'preview-viewer' ? { ...PREVIEW_INFO, can_write: false } : PREVIEW_INFO;
  return <ConsentForm info={info} requestId="preview" onFinish={() => {}} />;
}
